import React, { useEffect, useCallback, useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import { Map } from 'immutable'
import ImmutablePropTypes from 'react-immutable-proptypes'
import PropTypes from 'prop-types'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { getBoundActions } from '../../actions/index'
import { H1, H6, HEADING_TYPES } from '../Heading/index'
import AccountCreateForm from '../AccountCreateForm/index'

const PAGE_STATE_FORM = 'form'
const PAGE_STATE_ERROR = 'error'
const PAGE_STATE_SUCCESS = 'success'

function RemoteAccountCreatePage ({
  user,
  auth,
  location,
  staticText,
  remoteLoginCode,
  setRemoteLoginCode,
  setCheckoutCreationSource,
  setCheckoutAccountCreateData,
}) {
  const [pageState, setPageState] = useState(PAGE_STATE_FORM)
  const [errorTitle, setErrorTitle] = useState('Error')
  const [errorText, setErrorText] = useState('')

  const { query } = location || {}
  const { code, creationSource } = query
  const isLoggedIn = getAuthIsLoggedIn(auth)
  const userCreateProcessing = user.get('userCreateProcessing')
  const userLoginProcessing = auth.get('processing')
  const remoteLoginCodeProcessing = remoteLoginCode.get('approveRemoteLoginCodeProcessing')
  const remoteLoginCodeApproved = remoteLoginCode.getIn(['data', 'remoteLoginCodeSuccessfullyActivated'])
  const remoteLoginCodeError = remoteLoginCode.getIn(['data', 'approveRemoteLoginCodeErrorCode'])

  const isProcessing = userCreateProcessing || userLoginProcessing || remoteLoginCodeProcessing

  const handleFormSubmit = useCallback((data) => {
    setCheckoutAccountCreateData(data)
  }, [])

  useEffect(() => {
    if (code) {
      setRemoteLoginCode(code)
    }
    if (creationSource) {
      setCheckoutCreationSource(creationSource)
    }
  }, [code, creationSource])

  useEffect(() => {
    if (remoteLoginCodeApproved) {
      setPageState(PAGE_STATE_SUCCESS)
    } else if (remoteLoginCodeError) {
      setPageState(PAGE_STATE_ERROR)
      setErrorTitle(staticText.get('invalidLoginCodeErrorTitle'))
      setErrorText(staticText.get('alreadyLoggedInErrorText'))
    }
  }, [remoteLoginCode])

  useEffect(() => {
    if (isLoggedIn || !code) {
      setPageState(PAGE_STATE_ERROR)
      if (isLoggedIn) {
        setErrorTitle(staticText.get('alreadyLoggedInErrorTitle'))
        setErrorText(staticText.get('alreadyLoggedInErrorText'))
      } else if (!code) {
        setErrorTitle(staticText.get('invalidQRCodeErrorTitle'))
        setErrorText(staticText.get('invalidQRCodeErrorText'))
      }
    }
  }, [])

  if (pageState === PAGE_STATE_SUCCESS) {
    return (
      <div className="remote-account-create-page">
        <div className="remote-account-create-page__success">
          <H1 as={HEADING_TYPES.H3} className="remote-account-create-page__success-title">
            {staticText.get('accountCreated')}
          </H1>
          <p className="remote-account-create-page__success-text">
            {staticText.get('successMessage')}
          </p>
        </div>
      </div>
    )
  }

  if (pageState === PAGE_STATE_ERROR) {
    return (
      <div className="remote-account-create-page">
        <div className="remote-account-create-page__error">
          <H1 as={HEADING_TYPES.H3} className="remote-account-create-page__error-title">
            {errorTitle}
          </H1>
          <p className="remote-account-create-page__error-text">
            {errorText}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="remote-account-create-page">
      <div className="remote-account-create-page__form">
        <H1 as={HEADING_TYPES.H3} className="remote-account-create-page__form-title">
          {staticText.get('createAccount')}
        </H1>
        <AccountCreateForm
          location={location}
          onFormSubmit={handleFormSubmit}
          submitText={staticText.get('signUp')}
          isProcessing={isProcessing}
          hideLogin
        />
        {isProcessing
          ? null
          : <div className="remote-account-create-page__form-login">
            <p className="remote-account-create-page__form-login-text">
              {staticText.get('alreadyHaveAccount')}
            </p>
            <H6 className="remote-account-create-page__form-login-title">
              {staticText.get('logInOnTV')}
            </H6>
          </div>
        }
      </div>
    </div>
  )
}

RemoteAccountCreatePage.propTypes = {
  location: PropTypes.object.isRequired,
  user: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  remoteLoginCode: ImmutablePropTypes.map.isRequired,
  setCheckoutCreationSource: PropTypes.func.isRequired,
  setRemoteLoginCode: PropTypes.func.isRequired,
  getRemoteLoginCodeApproval: PropTypes.func.isRequired,
  setOverlayDialogVisible: PropTypes.func.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
}

export default connectRedux(
  (state) => ({
    auth: state.auth,
    user: state.user,
    remoteLoginCode: state.remoteLoginCode,
    staticText: state.staticText.getIn(['data', 'remoteAccountCreatePage', 'data'], Map()),
  }),
  (dispatch) => {
    const actions = getBoundActions(dispatch)
    return {
      setCheckoutAccountCreateData: actions.checkout.setCheckoutAccountCreateData,
      setCheckoutCreationSource: actions.checkout.setCheckoutCreationSource,
      setRemoteLoginCode: actions.remoteLoginCode.setRemoteLoginCode,
      getRemoteLoginCodeApproval: actions.remoteLoginCode.getRemoteLoginCodeApproval,
      setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
    }
  },
)(RemoteAccountCreatePage)
